<template lang="pug">
div
  j-master-dialog(
    :show="show"
    :master-data="itemData"
    :loading="loading"
    :is-add="isAdd"
    :delete-loading="deleteLoading"
    :label="isAdd ? $t('master.item.add') : $t('master.item.edit')"
    :label-name="$t('common.name')"
    :is-on-mobile="isOnMobile"
    :readOnly="readOnly"
    :isMaster="isMaster"
    :hide-fields="['full_name', 'code', 'name', 'keyword']"
    @on-close="closeDialog"
    @on-update="updateItem"
    @on-create="createItem"
    type="item"
    :width="800"
    height="480"
  )
    template(#middle="{ isDisabled }")
      v-row
        v-col(cols="12" sm="6").pr-md-4
          validation-provider(:name="$t('common.code')" :rules="{ required: true, numeric_code_rule: true }" v-slot="{ errors }")
            v-text-field#code.mt-4(
              v-next-focus
              hide-spin-buttons
              :error-messages="errors"
              :value="itemData.code"
              @input="onCodeInput"
              :label="$t('common.code')"
              :disabled="isDisabled"
            )
              template(v-slot:label)
                span {{$t("common.code")}}
                span.red--text *

          validation-provider(rules="required" v-slot="{ errors }" :name="$t('common.name')")
            v-text-field#name.mt-4(
              v-next-focus
              :error-messages="errors"
              v-model="itemData.name"
              :label="$t('common.name')"
              :disabled="isDisabled"
            )
              template(v-slot:label)
                span {{$t('common.name')}}
                span.red--text *

          validation-provider(:rules="{ required: true }" v-slot="{ errors }" :name="$t('master.item.short_name')")
            v-text-field#short_name.mt-4(
              v-next-focus
              :label="$t('master.item.short_name')"
              :error-messages="errors"
              v-model="itemData.short_name"
              :disabled="isDisabled"
            )
              template(#label)
                span {{$t('master.item.short_name')}}
                span.red--text *

          validation-provider(:rules="{ required: true, kana_name_rule: true }" v-slot="{ errors }" :name="$t('common.kana_name')")
            v-text-field#kana_name.mt-4(
              v-next-focus
              :label="$t('common.kana_name')"
              :error-messages="errors"
              v-model="itemData.kana_name"
              :disabled="isDisabled"
            )
              template(#label)
                span {{$t('common.kana_name')}}
                span.red--text *
        
        v-col(cols="12" sm="6")
          validation-provider(:rules="{ required: true }" v-slot="{ errors }" :name="$t('master.item.flower_type')")
            v-autocomplete#flower_type_id.mt-4(
              v-next-focus
              :items="flowerTypeOptions"
              :item-text="item => item.code + ': ' + item.name"
              item-value="id"
              :filter="(item, queryText) => filterBySearchString(item, queryText)"
              :label="$t('master.item.flower_type')"
              :error-messages="errors"
              v-model="itemData.flower_type_id"
              :disabled="isDisabled"
              :no-data-text="$t('message.no_data')"
              clearable
            )
              template(#label)
                span {{$t('master.item.flower_type')}}
                span.red--text *

          validation-provider(:rules="{ required: false }" v-slot="{ errors }" :name="$t('master.item.unit')")
            v-autocomplete#unit_id.mt-4(
              :key="`unit-${show}`"
              v-next-focus
              :label="$t('master.item.unit')"
              :items="units"
              :item-text="item => item.code + ': ' + item.name"
              item-value="id"
              :filter="(item, queryText) => filterBySearchString(item, queryText)"
              :error-messages="errors"
              v-model="itemData.unit_id"
              :disabled="isDisabled"
              :no-data-text="$t('message.no_data')"
              clearable
            )

          v-text-field#keyword.mt-4(
            v-next-focus
            :label="$t('common.keyword')"
            v-model="itemData.keyword"
            :disabled="isDisabled"
          )
</template>
<script>
import { defineComponent, toRefs, ref, watch, getCurrentInstance } from 'vue'
import JMasterDialog from '../JMasterDialog/index'
import { api } from '@/plugins'
import { endpoints, showError, filterBySearchString } from '@/utils'
import { useMasterDialog } from '@/components/JMasterDialog/useMasterDialog'
import { useResetData } from '@/composables/useResetData'
import { normalizeCode } from '@/utils/string'

const ItemDialog = defineComponent({
  components: {
    JMasterDialog
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: false,
      default: null
    },
    isAdd: {
      type: Boolean,
      required: true,
      default: true
    },
    isOnMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isMaster: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { $toast, $root } = instance.proxy
    const loading = ref(false)
    const deleteLoading = ref(false)
    const { item } = toRefs(props)
    const { masterData: itemData, initializeData } = useMasterDialog()
    const { resetMasterData } = useResetData()
    const units = ref([])
    const flowerTypeOptions = ref([])

    const fetchFlowerTypes = async () => {
      if (flowerTypeOptions.value.length > 0) return
      try {
        const { data } = await api.get(endpoints.MASTER_FLOWER_TYPE, {
          params: { active: true }
        })
        flowerTypeOptions.value = data
      } catch (e) {
        console.error(e)
      }
    }

    const fetchUnits = async () => {
      if (units.value.length > 0) return
      try {
        const { data } = await api.get(endpoints.MASTER_UNIT, {
          params: { per_page: 100, active: true }
        })
        units.value = data
      } catch (e) {
        console.error(e)
      }
    }

    const closeDialog = () => {
      loading.value = false
      deleteLoading.value = false
      resetMasterData(initializeData)
      emit('on-close')
    }

    const updateItem = async (isThrow = false) => {
      loading.value = true
      const id = itemData.value.id
      if (!id) {
        $toast.error($root.$t('message.update_failed'))
        loading.value = false
        return
      }
      try {
        const params = itemData.value
        await api.put(`${endpoints.MASTER_ITEM}/${id}`, params)
        $toast.success($root.$t('message.update_successful'))
        emit('on-reload')
        closeDialog()
      } catch (e) {
        if (isThrow) throw e
        showError(e, $toast, $root.$t('message.update_failed'))
      } finally {
        loading.value = false
      }
    }

    const createItem = async () => {
      loading.value = true
      try {
        const { data } = await api.post(endpoints.MASTER_ITEM, itemData.value)
        $toast.success($root.$t('message.create_successful'))
        emit('on-reload', data)
        closeDialog()
      } catch (e) {
        showError(e, $toast, $root.$t('message.create_failed'))
      } finally {
        loading.value = false
      }
    }

    const onCodeInput = (value) => {
      itemData.value.code = normalizeCode(value)
    }

    watch(
      () => props.show,
      async () => {
        if (props.show) {
          fetchUnits()
          fetchFlowerTypes()
          const defaultItem = {
            code: null,
            name: null,
            short_name: null,
            kana_name: null,
            flower_type_id: null,
            unit_id: null,
            keyword: null
          }
          initializeData({
            initialData: item.value,
            defaultData: defaultItem,
            isAdd: props.isAdd
          })
        }
      }
    )

    return {
      closeDialog,
      loading,
      itemData,
      deleteLoading,
      updateItem,
      createItem,
      units,
      flowerTypeOptions,
      onCodeInput,
      filterBySearchString
    }
  }
})

export default ItemDialog
</script>
